import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerElementBase } from '../elements/element-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';

export class SelectionManager extends ManagerBase {
  private isDrawingMarquee: boolean = false;
  private marqueeStartX: number = 0;
  private marqueeStartY: number = 0;
  private marqueeEndX: number = 0;
  private marqueeEndY: number = 0;
  private lastMouseDownX: number = 0;
  private lastMouseDownY: number = 0;
  private lastMouseDownTime: number = 0;
  private readonly DOUBLE_CLICK_DELAY: number = 300; // milliseconds

  constructor(
    levelDesigner: LevelDesignerComponent,
    canvas: HTMLCanvasElement,
  ) {
    super(levelDesigner, canvas);
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  public override handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.clearSelection();
      this.levelDesigner.redraw();
    }
  }

  public destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }

  public override handleMouseDown(event: MouseEvent): MouseEventResult | undefined {
    console.log('Mouse down event:', event);
    if (event.button === 1) return undefined;
    if (event.button === 0) { // Left mouse button
      const mousePos = this.getMousePosition(event);

      // Start potential new selection
      this.startMarqueeSelection(mousePos.x, mousePos.y);
      return { cursor: 'crosshair', stopPropagation: false, redraw: true };
    }
    return undefined;
  }

  public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
    if (this.isDrawingMarquee) {
      const mousePos = this.getMousePosition(event);
      this.updateMarqueeSelection(mousePos.x, mousePos.y);
      this.levelDesigner.redraw();
      return { cursor: 'crosshair', stopPropagation: true, redraw: true };
    }
    return undefined;
  }

  public override handleMouseUp(event: MouseEvent): MouseEventResult | undefined {
    if (this.isDrawingMarquee) {
      this.finishMarqueeSelection(event.shiftKey);
      return { cursor: 'default', stopPropagation: true, redraw: true };
    }
    return undefined;
  }

  public override OnDraw(): void {
    this.drawMarquee();
    this.drawSelectionBoundingBox();
    this.drawLockIcons();
  }

  private startMarqueeSelection(x: number, y: number): void {
    this.isDrawingMarquee = true;
    this.marqueeStartX = this.marqueeEndX = x;
    this.marqueeStartY = this.marqueeEndY = y;
  }

  private updateMarqueeSelection(x: number, y: number): void {
    if (this.isDrawingMarquee) {
      this.marqueeEndX = x;
      this.marqueeEndY = y;
    }
  }

  private finishMarqueeSelection(isShiftPressed: boolean): void {
    if (!this.isDrawingMarquee) return;

    this.isDrawingMarquee = false;
    if (!isShiftPressed) {
      this.clearSelection();
    }

    // Check if it's a click (no drag) or a marquee selection
    if (this.marqueeStartX === this.marqueeEndX && this.marqueeStartY === this.marqueeEndY) {
      this.selectTopElementAtPoint(this.marqueeStartX, this.marqueeStartY);
    } else {
      this.selectElementsInMarquee();
    }

    this.levelDesigner.redraw();
  }

  private drawMarquee(): void {
    if (!this.isDrawingMarquee) return;

    const left = Math.min(this.marqueeStartX, this.marqueeEndX);
    const top = Math.min(this.marqueeStartY, this.marqueeEndY);
    const width = Math.abs(this.marqueeEndX - this.marqueeStartX);
    const height = Math.abs(this.marqueeEndY - this.marqueeStartY);

    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(0, 0, 255, 0.5)';
    this.ctx.fillStyle = 'rgba(0, 0, 255, 0.1)';
    this.ctx.lineWidth = 1 / this.levelDesigner.getScale();
    this.ctx.strokeRect(left, top, width, height);
    this.ctx.fillRect(left, top, width, height);
    this.ctx.restore();
  }

  private drawSelectionBoundingBox() {
    if (!this.ctx || this.levelDesigner.selectedElements.length === 0) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const element of this.levelDesigner.selectedElements) {
      const bounds = element.getBoundingBox();
      minX = Math.min(minX, bounds.x);
      minY = Math.min(minY, bounds.y);
      maxX = Math.max(maxX, bounds.x + bounds.width);
      maxY = Math.max(maxY, bounds.y + bounds.height);
    }

    // Expand the bounding box by 5 pixels in each direction
    const expansion = 15 / this.levelDesigner.getScale();
    minX -= expansion;
    minY -= expansion;
    maxX += expansion;
    maxY += expansion;

    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(0, 0, 255, 0.5)'; // Blue with 0.5 transparency
    this.ctx.lineWidth = 2 / this.levelDesigner.getScale();
    this.ctx.setLineDash([5 / this.levelDesigner.getScale(), 5 / this.levelDesigner.getScale()]);
    this.ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
    this.ctx.restore();
  }

  private selectElementsInMarquee(): void {
    const left = Math.min(this.marqueeStartX, this.marqueeEndX);
    const top = Math.min(this.marqueeStartY, this.marqueeEndY);
    const right = Math.max(this.marqueeStartX, this.marqueeEndX);
    const bottom = Math.max(this.marqueeStartY, this.marqueeEndY);

    let selectedElements: LevelDesignerElementBase[] = [];

    // First pass: check for line intersections and containment
    for (const element of this.levelDesigner.elements) {
        const lines = element.getLineSegments();
        const hasIntersectingLines = lines.some(line => {
            return this.lineIntersectsRect(
                line.x1, line.y1, line.x2, line.y2,
                left, top, right, bottom
            );
        });

        const bounds = element.getBoundingBox();
        const isContained = 
            bounds.x >= left &&
            bounds.x + bounds.width <= right &&
            bounds.y >= top &&
            bounds.y + bounds.height <= bottom;

        if (hasIntersectingLines || isContained) {
            selectedElements.push(element);
        }
    }

    // If no elements were selected, check if the marquee is inside any element
    if (selectedElements.length === 0) {
        // Check from top-most (last) to bottom-most (first) element
        for (let i = this.levelDesigner.elements.length - 1; i >= 0; i--) {
            const element = this.levelDesigner.elements[i];
            if (element.isPointInside(left + (right - left) / 2, top + (bottom - top) / 2)) {
                selectedElements.push(element);
                break; // Only select the top-most containing element
            }
        }
    }

    // Add all found elements to selection
    this.addToSelection(selectedElements);

    // Make sure this line exists and is called when elements are selected
    this.levelDesigner.setSelectedElements(this.levelDesigner.selectedElements);
  }

  private lineIntersectsRect(
    x1: number, y1: number, x2: number, y2: number,
    rectLeft: number, rectTop: number, rectRight: number, rectBottom: number
  ): boolean {
    // Check if either endpoint is inside the rectangle
    if (this.pointInRect(x1, y1, rectLeft, rectTop, rectRight, rectBottom) ||
        this.pointInRect(x2, y2, rectLeft, rectTop, rectRight, rectBottom)) {
      return true;
    }

    // Check intersection with each edge of the rectangle
    const lines = [
      { x1: rectLeft, y1: rectTop, x2: rectRight, y2: rectTop },       // Top
      { x1: rectRight, y1: rectTop, x2: rectRight, y2: rectBottom },   // Right
      { x1: rectRight, y1: rectBottom, x2: rectLeft, y2: rectBottom }, // Bottom
      { x1: rectLeft, y1: rectBottom, x2: rectLeft, y2: rectTop }      // Left
    ];

    return lines.some(edge => this.lineSegmentsIntersect(
      x1, y1, x2, y2,
      edge.x1, edge.y1, edge.x2, edge.y2
    ));
  }

  private pointInRect(
    x: number, y: number,
    rectLeft: number, rectTop: number, rectRight: number, rectBottom: number
  ): boolean {
    return x >= rectLeft && x <= rectRight && y >= rectTop && y <= rectBottom;
  }

  private lineSegmentsIntersect(
    x1: number, y1: number, x2: number, y2: number,
    x3: number, y3: number, x4: number, y4: number
  ): boolean {
    const denominator = ((x2 - x1) * (y4 - y3)) - ((y2 - y1) * (x4 - x3));
    if (denominator === 0) return false;

    const ua = (((x4 - x3) * (y1 - y3)) - ((y4 - y3) * (x1 - x3))) / denominator;
    const ub = (((x2 - x1) * (y1 - y3)) - ((y2 - y1) * (x1 - x3))) / denominator;

    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
  }

  private addToSelection(elements: LevelDesignerElementBase[]): void {
    elements.forEach(element => {
      if (!this.levelDesigner.selectedElements.includes(element)) {
        this.levelDesigner.selectedElements.push(element);
        element.select();
      }
    });
    
  }

  private clearSelection(): void {
    this.levelDesigner.selectedElements.forEach(element => element.deselect());
    this.levelDesigner.setSelectedElements([]);
  }

  private drawLockIcons(): void {
    if (!this.ctx) return;

    const lockSize = 18 / this.levelDesigner.getScale();
    const shackleOffset = 5 / this.levelDesigner.getScale(); // New offset for the shackle

    this.ctx.save();
    this.ctx.strokeStyle = '#555555';
    this.ctx.lineWidth = 2 / this.levelDesigner.getScale();

    for (const element of this.levelDesigner.selectedElements) {
      if (element.isLocked()) {
        const bounds = element.getBoundingBox();
        const centerX = bounds.x + bounds.width / 2;
        const centerY = bounds.y + bounds.height / 2;

        // Draw lock body
        this.ctx.fillStyle = 'rgba(50, 50, 50, 0.5)';
        this.ctx.beginPath();
        this.ctx.moveTo(centerX - lockSize / 2, centerY - lockSize / 4);
        this.ctx.lineTo(centerX + lockSize / 2, centerY - lockSize / 4);
        this.ctx.lineTo(centerX + lockSize / 2, centerY + lockSize / 2);
        this.ctx.lineTo(centerX - lockSize / 2, centerY + lockSize / 2);
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.stroke();

        // Draw lock shackle (moved up by shackleOffset)
        this.ctx.fillStyle = 'transparent';// 'rgba(80, 80, 80, 0.5)';
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY - lockSize / 4 - shackleOffset, lockSize / 3, Math.PI, 2 * Math.PI);
        this.ctx.lineTo(centerX + lockSize / 3, centerY - lockSize / 4 - shackleOffset);
        this.ctx.lineTo(centerX + lockSize / 3, centerY - shackleOffset);
        this.ctx.lineTo(centerX - lockSize / 3, centerY - shackleOffset);
        this.ctx.lineTo(centerX - lockSize / 3, centerY - lockSize / 4 - shackleOffset);
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.stroke();

        // Draw keyhole
        this.ctx.fillStyle = 'rgba(30, 30, 30, 0.9)';
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY + lockSize / 6, lockSize / 10, 0, 2 * Math.PI);
        this.ctx.fill();
      }
    }

    this.ctx.restore();
  }

  // Modified method to get the top element at a point
  private getTopElementAtPoint(x: number, y: number): LevelDesignerElementBase | null {
    for (let i = this.levelDesigner.elements.length - 1; i >= 0; i--) {
      const element = this.levelDesigner.elements[i];
      if (element.isPointInside(x, y)) {
        return element;
      }
    }
    return null;
  }

  private removeFromSelection(element: LevelDesignerElementBase): void {
    const index = this.levelDesigner.selectedElements.indexOf(element);
    if (index !== -1) {
      this.levelDesigner.selectedElements.splice(index, 1);
      element.deselect();
      this.levelDesigner.setSelectedElements(this.levelDesigner.selectedElements);
    }
  }

  private selectTopElementAtPoint(x: number, y: number): void {
    const element = this.getTopElementAtPoint(x, y);
    if (element) {
      // If element is already selected, remove it; otherwise add it
      if (!this.levelDesigner.selectedElements.includes(element)) {
        this.addToSelection([element]);
        this.levelDesigner.setSelectedElements(this.levelDesigner.selectedElements);
      }
    }
  }

  public override handleDoubleClick(event: MouseEvent): MouseEventResult | undefined {
    const mousePos = this.getMousePosition(event);
    const clickedElement = this.getTopElementAtPoint(mousePos.x, mousePos.y);
    if (!clickedElement) return undefined;

    // Get the type of the clicked element
    const elementType = clickedElement.constructor.name;

    // Select all visible elements of the same type
    const elementsOfType = this.levelDesigner.elements.filter(element => 
      element.constructor.name === elementType && 
      this.isElementVisible(element)
    );

    // Clear current selection and select all elements of the same type
    this.clearSelection();
    this.addToSelection(elementsOfType);
    this.levelDesigner.setSelectedElements(this.levelDesigner.selectedElements);

    return { cursor: 'default', stopPropagation: true, redraw: true };
  }

  private isElementVisible(element: LevelDesignerElementBase): boolean {
    const bounds = element.getBoundingBox();
    const canvas = this.canvas;
    
    // Convert canvas bounds to world coordinates
    const canvasLeft = -this.levelDesigner.getOffsetX() / this.levelDesigner.getScale();
    const canvasTop = -this.levelDesigner.getOffsetY() / this.levelDesigner.getScale();
    const canvasRight = canvasLeft + canvas.width / this.levelDesigner.getScale();
    const canvasBottom = canvasTop + canvas.height / this.levelDesigner.getScale();

    // Check if element's bounds intersect with canvas bounds
    return !(
      bounds.x + bounds.width < canvasLeft ||
      bounds.x > canvasRight ||
      bounds.y + bounds.height < canvasTop ||
      bounds.y > canvasBottom
    );
  }
}
